import { useId, useMemo } from "react";
import { TrashIcon } from "lucide-react";
import { Button } from "@/components/Base/Button";
import { Input } from "@/components/Base/Input";
import { Select } from "@/components/Common/Select";
import { Tooltip } from "@/components/Common/Tooltip";
import { Option } from "@/types/v2/UI";
import {
  encodeShowToastActionValue,
  parseShowToastActionValue,
  ShowToastActionValue as ShowToastValue,
  TOAST_FIELD_KEYS,
  TOAST_FIELD_SLOTS,
} from "@/lib/automationShowToast";
import {
  ActionFieldLabel,
  ACTION_FIELD_LABEL_CLASS,
} from "@/components/Automations/ActionFieldLabel";

const NONE_VALUE = "__none__";

const FIELD_OPTIONS: Option[] = [
  { label: "— none —", value: NONE_VALUE },
  ...TOAST_FIELD_KEYS.map((k) => ({ label: k, value: k })),
];

export interface ShowToastActionValueProps {
  label: string;
  value: string | number | null;
  onChange?: (raw: string) => void;
  onRemove?: () => void;
  canRemove?: boolean;
  readOnly?: boolean;
}

export const ShowToastActionValue = ({
  label,
  value,
  onChange,
  onRemove,
  canRemove = true,
  readOnly,
}: ShowToastActionValueProps) => {
  const parsed = useMemo(() => parseShowToastActionValue(value), [value]);

  // Before the early return: hooks cannot be called conditionally.
  const fieldId = useId();
  const headlineId = `${fieldId}-headline`;
  const fieldsLabelId = `${fieldId}-fields-label`;

  const isReadOnly = readOnly || !onChange;

  const emit = (next: ShowToastValue) =>
    onChange?.(encodeShowToastActionValue(next));

  const setField = (index: number, raw: string) => {
    const fields = [...parsed.fields];
    fields[index] =
      raw === NONE_VALUE ? null : (raw as ShowToastValue["fields"][number]);
    emit({ ...parsed, fields });
  };

  if (isReadOnly) {
    const chosen = parsed.fields.filter(Boolean);
    return (
      <div
        className={"flex flex-row items-center gap-2 text-base text-primary"}
      >
        <span>{label}:</span>
        <span>
          {`"${parsed.headline || "(no headline)"}"`}
          {chosen.length ? ` · ${chosen.join(" · ")}` : " · no fields"}
        </span>
      </div>
    );
  }

  return (
    <div className={"flex flex-col gap-3 rounded-lg border border-border p-3"}>
      <div className={"flex flex-row items-center justify-between"}>
        <span className={"text-base text-primary"}>{label}</span>
        {canRemove && (
          <Tooltip message={"Remove"}>
            <Button size="icon" variant="destructive-alt" onClick={onRemove}>
              <TrashIcon />
            </Button>
          </Tooltip>
        )}
      </div>

      <div className={"flex flex-col gap-1"}>
        <ActionFieldLabel htmlFor={headlineId} required>
          {"Headline"}
        </ActionFieldLabel>
        <Input
          id={headlineId}
          variant={"minimal"}
          placeholder={"e.g. Promise time approaching"}
          value={parsed.headline}
          onChange={(e) => emit({ ...parsed, headline: e.target.value })}
        />
      </div>

      {/*
       * A group label, not an ActionFieldLabel: <label for> can only point at
       * one of the three selects, and that one would then be named twice.
       * role="group" + aria-labelledby names all three at once, and each select
       * still carries its own aria-label. Wired, not decorative.
       */}
      <div
        aria-labelledby={fieldsLabelId}
        className={"flex flex-col gap-1"}
        role={"group"}
      >
        <span className={ACTION_FIELD_LABEL_CLASS} id={fieldsLabelId}>
          {"Card fields to show"}
        </span>
        <div className={"flex flex-row gap-2"}>
          {Array.from({ length: TOAST_FIELD_SLOTS }, (_, i) => (
            <Select
              key={i}
              selectTriggerProps={{
                variant: "minimal",
                "aria-label": `Card field ${i + 1}`,
              }}
              options={FIELD_OPTIONS}
              value={parsed.fields[i] ?? NONE_VALUE}
              onValueChange={(v) => setField(i, v)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
